import { useState } from "react";
import { CheckCircle2, Check, Calendar, CalendarPlus } from "lucide-react";
import { calendarItems } from "@/lib/calendarLinks";
import { DeedInvite } from "@/components/DeedInvite";
import { fmtDate, toInputDate, fmtCurrency, fmtAddress } from "@/lib/format";
import { netCommission } from "@/components/PartilhaComissao";
import { DateInput } from "@/components/DateInput";
import { DeedFields } from "@/components/NextStep";
import { MenuOrSheet } from "@/components/ActionSheet";
import { HelpButton } from "@/components/HelpButton";
import { CountList } from "@/components/CountList";
import { CardTag } from "@/components/CardTag";
import { gpTrata } from "@/components/gpView";
import { PhaseSegments, PHASE_NAMES, STATE } from "@/components/ProcessCard";

const PHASES = ["Angariação", "CMI assinado", "Proposta aceite", "CPCV", "Escritura"];
const ok = (s) => ["validado", "na"].includes(s.status);

export function currentPhase(p) {
  if (p.status === "escriturado") return 4;
  if (p.stage === "angariacao") return 0;
  const step = (n) => p.steps.find((s) => s.num === n);
  if (step(5) && !ok(step(5))) return 2;
  if (p.steps.some((s) => s.num !== 5 && s.num > 1 && s.num < 10 && !ok(s))) return 3;
  return 4;
}

export function PhaseBar({ p, className = "mt-5" }) {
  if (p.card) {
    const c = p.card;
    return (
      <div className={className} data-testid="phase-bar">
        <PhaseSegments phase={c.phase} tone={c.tone} testid="phase-segments" />
        <div className="grid grid-cols-5 gap-1.5 mt-2">
          {PHASE_NAMES.map((n, i) => (
            <div key={n} data-testid={`phase-${i}`} data-state={i < c.phase ? "done" : i === c.phase ? "current" : "todo"}
              className={`text-xs sm:text-sm leading-tight ${i === c.phase ? "font-semibold navy-text col-span-5 sm:col-span-1" : i < c.phase ? "text-slate-600 hidden sm:block" : "text-slate-400 hidden sm:block"}`}>{i === c.phase ? c.phase_label : n}</div>
          ))}
        </div>
      </div>
    );
  }
  const cur = currentPhase(p);
  return (
    <div className={`grid grid-cols-4 gap-2 ${className}`} data-testid="phase-bar">
      {PHASES.map((name, i) => (
        <div key={name} data-testid={`phase-${i}`} data-state={i < cur ? "done" : i === cur ? "current" : "todo"}>
          <div className={`h-1 rounded-full ${i < cur ? "bg-green-700" : i === cur ? "bg-ec-green" : "bg-slate-200"}`} />
          <div className={`mt-2 text-xs sm:text-sm truncate ${i < cur ? "text-green-700" : i === cur ? "text-ec-green font-semibold" : "text-slate-400"}`}>{name}</div>
        </div>
      ))}
    </div>
  );
}

function DeedWhere({ p, onSaveDeed }) {
  const [edit, setEdit] = useState(false);
  const [time, setTime] = useState(p.deed_time || "");
  const [place, setPlace] = useState(p.deed_place || "");
  const [guests, setGuests] = useState([]);
  const txt = [p.deed_time, p.deed_place].filter(Boolean).join(" · ");
  if (!edit) {
    return <button type="button" disabled={!onSaveDeed} onClick={() => setEdit(true)} className="mt-1 block text-sm text-white/75 enabled:hover:text-white text-left" data-testid="deed-where">{txt || (onSaveDeed ? "+ hora, local e convidados" : "")}</button>;
  }
  const persist = () => { onSaveDeed(null, { deed_time: time || null, deed_place: place.trim() || null }); setEdit(false); };
  const items = calendarItems({ uid: `escritura-${p.id}`, title: `Escritura · ${p.card?.address || p.ref}`, date: String(p.deed_date).slice(0, 10), time, place: place.trim(), guests, prefix: "deed-cal-send" })
    .map((it) => ({ ...it, onClick: () => { it.onClick(); persist(); } }));
  return (
    <div className="mt-2 space-y-3 text-slate-800" data-testid="deed-where-edit">
      <DeedFields time={time} setTime={setTime} place={place} setPlace={setPlace} />
      <DeedInvite p={p} guests={guests} setGuests={setGuests} />
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <button type="button" onClick={persist} className="px-3 py-1.5 rounded-md bg-white text-ec-green font-semibold" data-testid="deed-where-save">Guardar</button>
        <MenuOrSheet title="Enviar para o calendário" testid="deed-cal-send-menu" align="start" items={items}
          trigger={<button type="button" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/50 text-white font-semibold hover:bg-white/10" data-testid="deed-cal-send"><CalendarPlus size={14} /> Guardar e enviar para o calendário</button>} />
        <button type="button" onClick={() => setEdit(false)} className="text-white/70 hover:text-white" data-testid="deed-where-cancel">cancelar</button>
      </div>
    </div>
  );
}

function DeedBox({ p, onSaveDeed, cal }) {
  const [edit, setEdit] = useState(false);
  if (p.status === "escriturado") {
    return (
      <div className="rounded-2xl bg-ec-green text-white px-5 py-4 min-w-[250px] self-start" data-testid="deed-box">
        <div className="text-[15px] text-white/75">Escritura realizada a</div>
        <div className="font-semibold text-2xl leading-tight" data-testid="deed-done-date">{p.deed_date ? fmtDate(p.deed_date) : "—"}</div>
      </div>
    );
  }
  if (!p.deed_date) {
    if (!onSaveDeed) return <span className="self-start text-sm font-medium px-3 py-2 rounded-[12px] bg-slate-100 text-slate-600" data-testid="deed-box"><span data-testid="deed-none">Escritura por marcar</span></span>;
    if (!edit) {
      return (
        <button type="button" onClick={() => setEdit(true)} data-testid="deed-box"
          className="self-start inline-flex items-center gap-2 h-11 px-5 rounded-[14px] bg-ec-green text-white text-[15px] font-semibold hover:bg-ec-greenDark transition-colors whitespace-nowrap">
          <Calendar size={18} /><span data-testid="deed-date-label">Marcar escritura</span>
        </button>
      );
    }
    return (
      <div className="self-start flex items-center gap-2 rounded-[14px] bg-ec-green px-3 py-2" data-testid="deed-box">
        <DateInput dark autoFocus data-testid="deed-date" className="w-40 text-xs" onChange={(e) => { if (e.target.value) { onSaveDeed(e.target.value); setEdit(false); } }} />
        <button type="button" onClick={() => setEdit(false)} className="text-xs text-white/70 hover:text-white" data-testid="deed-date-cancel">cancelar</button>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-ec-green text-white px-5 py-4 min-w-[250px] max-w-[440px] self-start" data-testid="deed-box">
    <div className="flex items-center gap-4">
      <button type="button" onClick={() => onSaveDeed && setEdit(true)} disabled={!onSaveDeed} data-testid="deed-edit-btn"
        className="font-semibold text-5xl leading-none tabular-nums enabled:hover:text-ec-gold transition-colors disabled:cursor-default" title={onSaveDeed ? "Alterar a data da escritura" : undefined}>
        {p.days_to_deed ?? "—"}
      </button>
      <div className="min-w-0">
        <div className="text-[15px]">{p.deed_overdue ? "a escritura já passou: confirmar" : p.days_to_deed === 1 ? "dia até à escritura" : "dias até à escritura"}</div>
        {edit
          ? <div className="flex items-center gap-2 mt-1">
              <DateInput dark autoFocus defaultValue={toInputDate(p.deed_date)} data-testid="deed-date" className="w-40 text-xs"
                onChange={(e) => { if (e.target.value) { onSaveDeed(e.target.value); setEdit(false); } }} />
              <button type="button" onClick={() => setEdit(false)} className="text-xs text-white/70 hover:text-white" data-testid="deed-date-cancel">cancelar</button>
            </div>
          : <button type="button" onClick={() => setEdit(true)} className="text-sm text-white/75 hover:text-white" data-testid="deed-date-label">
              {p.deed_date ? fmtDate(p.deed_date) : "Data por marcar"}{onSaveDeed ? " · alterar" : ""}
            </button>}
        {p.deed_date && <DeedWhere p={p} onSaveDeed={onSaveDeed} />}
      </div>
    </div>
      {p.calendar?.changed && cal && (
        <div className="mt-2 text-xs text-amber-200 flex flex-wrap items-center gap-x-1" data-testid="deed-cal-changed">
          Data alterada ·
          <MenuOrSheet title="Atualizar calendário" testid="deed-cal-menu" align="start"
            trigger={<button className="underline hover:text-white" data-testid="deed-cal-update">Atualizar calendário</button>}
            items={[{ label: "Google Calendar", onClick: cal.google, testid: "deed-cal-google" }, { label: "Outlook / iPhone (.ics)", onClick: cal.ics, testid: "deed-cal-ics" }]} />
        </div>
      )}
    </div>
  );
}

export function GoalTags({ p, filter, setFilter }) {
  const goals = [];
  const deed = p.todo?.deed || [];
  const status = (n) => (n ? <span className="text-amber-700">{n === 1 ? "falta" : "faltam"} {n}</span> : <span className="inline-flex items-center gap-1">pronto <Check size={14} strokeWidth={2.5} /></span>);
  const tone = (n, on) => (n ? `${on ? "border-slate-700 bg-slate-50" : "border-slate-200 hover:bg-slate-50"} navy-text` : `border-green-300 bg-green-50 text-green-800 ${on ? "ring-2 ring-green-300" : "hover:bg-green-100"}`);
  if (p.status === "escriturado") return null;
  const trata = gpTrata(p);
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid="goal-tags">
      {goals.map(([id, label, keys]) => {
        const n = new Set(keys).size;
        const on = filter?.id === id;
        return (
          <button key={id} onClick={() => setFilter(on ? null : { id, label: label.toLowerCase(), keys: new Set(keys) })} data-testid={`goal-${id}`}
            className={`text-sm font-semibold px-3 py-1.5 rounded-lg border transition-colors ${tone(n, on)}`}>
            {label} · {status(n)}
          </button>
        );
      })}
      {p.card && trata ? (
        <>
          <span className={`text-sm font-semibold px-3 py-1.5 rounded-lg inline-flex ${STATE[p.card.tone]?.tag || ""}`} data-testid="goal-card-tag">
            {`${(p.card.top_tag ?? p.card.tag_main ?? p.card.tag ?? "").replace(/ \([^)]*\)$/, "")} · ${trata}`}</span>
        </>
      ) : p.card ? (
        <CountList pid={p.id} items={p.card.phase >= 2 ? deed : []} title="Impede a escritura" testid="goal-escritura" plain className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${STATE[p.card.tone]?.tag || ""}`}>
          <span className={`text-sm font-semibold px-3 py-1.5 rounded-lg inline-flex ${STATE[p.card.tone]?.tag || ""}`} data-testid="goal-card-tag">{p.card.top_tag ?? p.card.tag_main ?? p.card.tag}</span>
        </CountList>
      ) : (
        <CountList pid={p.id} items={deed} title="Impede a escritura" testid="goal-escritura" plain
          className={`text-sm font-semibold px-3 py-1.5 rounded-lg border transition-colors ${tone(deed.length)}`}>
          <span className={deed.length ? "" : `text-sm font-semibold px-3 py-1.5 rounded-lg border inline-flex ${tone(0)}`} data-testid={deed.length ? undefined : "goal-escritura"}>Para escritura · {status(deed.length)}</span>
        </CountList>
      )}
      {p.card && !trata && <CardTag pid={p.id} card={p.card} extrasOnly testid="goal-card" />}
    </div>
  );
}

const mediationLine = (p) => {
  const m = p.mediation || {};
  const pct = m.commission_type !== "valor" && m.commission_value != null ? ` (${m.commission_value}%)` : "";
  const signed = m.signed_date && (p.documents || []).find((d) => d.key === "contrato_mediacao")?.status === "recebido";
  return [m.exclusive == null ? null : m.exclusive ? "Com exclusividade" : "Sem exclusividade", signed ? "CMI assinado" : p.card?.corner?.small === "por gerar" ? "CMI por gerar" : "CMI por assinar", pct];
};

export function ProcessHeader({ p, onSaveDeed, menu, side, tags, banner, lead, hideSummary = false }) {
  const ang = p.stage === "angariacao";
  const [excl, cmi, pct] = mediationLine(p);
  const price = p.price || p.mediation?.asking_price;
  return (
    <div className="bg-white rounded-[18px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden" data-testid="process-header">
    {banner && (
      <div className="flex items-center gap-3 px-6 md:px-7 py-3.5 bg-green-50 border-b border-green-100 text-green-800 font-semibold text-[15px] animate-in fade-in slide-in-from-top-1 duration-500" data-testid="ready-banner">
        <CheckCircle2 size={22} className="shrink-0 text-white fill-green-600" /> {banner}
      </div>
    )}
    <div className="p-6 md:p-7 flex flex-col lg:flex-row lg:flex-wrap xl:flex-nowrap gap-6">
      <div className="flex-1 min-w-0 lg:basis-full xl:basis-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 tabular-nums" data-testid="process-num">{p.card?.num || p.ref}</span>
          <span className="text-sm font-medium px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 capitalize" data-testid="stage-badge">{p.mediation?.negocio || "venda"}</span>
          {p.property_ref && <span className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600" data-testid="property-ref">Ref. {p.property_ref}</span>}
          {p.has_pep && <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-600 text-white" data-testid="pep-badge" title={p.pep_approval ? `Aprovado por ${p.pep_approval.by}` : "Aprovação da direção pendente"}>PEP</span>}
          <HelpButton num={p.card?.num || p.ref} />
        </div>
        <h1 className="font-heading text-2xl md:text-3xl leading-tight navy-text mt-1.5">{p.card ? [p.card.prefix, p.card.address || "Morada por preencher"].filter(Boolean).join(" · ") : <>{p.title_prefix && p.property_address ? `${p.title_prefix} · ` : ""}{fmtAddress(p.property_address) || "Morada por preencher"}</>}</h1>
        {p.card?.locality && <div className="text-[15px] text-slate-500 mt-0.5" data-testid="header-locality">{p.card.locality}</div>}
        <div className="mt-1 text-[15px] text-slate-600" data-testid="deal-summary">
          {price && <>{p.price ? "Preço" : "Preço pedido"} <b className="navy-text">{fmtCurrency(price)}</b></>}
          {netCommission(p) != null && <> · Comissão <b className="navy-text" data-testid="deal-commission">{fmtCurrency(netCommission(p))}</b>{pct}</>}
          {ang && excl && <> · {excl}</>}{ang && <> · {cmi}</>}
        </div>
        {lead && <div className="mt-4 [&_[data-testid=next-step-btn]]:w-full" data-testid="header-lead">{lead}</div>}
        <PhaseBar p={p} />
        {tags}
        {p.card?.counts?.summary && !hideSummary && <p className={`mt-4 text-[16px] leading-relaxed ${(p.card.blocks || []).length ? "text-[#B91C1C] font-medium" : "text-slate-700"}`} data-testid="process-summary">{p.card.counts.summary}</p>}
      </div>
      {!ang && <DeedBox p={p} onSaveDeed={onSaveDeed} cal={menu.cal} />}
      <div className="flex flex-col gap-2 lg:w-72 shrink-0" data-testid="header-side">{side}</div>
    </div>
    </div>
  );
}
